import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Music, Play, Pause, Flame, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (!u ? u : /^https?:\/\//i.test(u) ? u : `${BACKEND}${u}`);

const fmt = (n) => {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(1)}K`.replace(".0K", "K");
  return `${(n / 1000000).toFixed(1)}M`.replace(".0M", "M");
};

/** "Sounds by @creator" horizontal row on the Profile page.
 *  Renders nothing when the user hasn't published any original sounds. */
export default function CreatorSounds({ username, isSelf = false }) {
  const [tracks, setTracks] = useState(null); // null=loading, []=empty, [...]=data
  const [previewId, setPreviewId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    api
      .get(`/users/${encodeURIComponent(username)}/sounds?limit=12`)
      .then((r) => {
        if (!cancelled) setTracks(r.data?.tracks || []);
      })
      .catch(() => !cancelled && setTracks([]));
    return () => {
      cancelled = true;
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [username]);

  const togglePreview = (t) => {
    if (previewId === t.id) {
      audioRef.current?.pause();
      setPreviewId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(resolveUrl(t.url));
    audio.crossOrigin = "anonymous";
    audio.play().catch(() => toast.error("Preview blocked by browser"));
    audio.onended = () => setPreviewId(null);
    audioRef.current = audio;
    setPreviewId(t.id);
  };

  if (tracks === null) {
    return (
      <div className="px-6 py-4 flex items-center gap-2 text-white/40">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#FF007F]" />
        <p className="font-mono text-[10px] uppercase tracking-widest">
          loading sounds…
        </p>
      </div>
    );
  }
  if (!tracks.length) return null;

  return (
    <section
      className="px-6 pt-6 pb-2"
      data-testid="creator-sounds-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <Music className="h-4 w-4 text-[#FF007F]" strokeWidth={2} />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40">
          sounds by @{username}
        </p>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-white/25">
          // originals · {tracks.length}
        </span>
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 snap-x snap-mandatory"
        data-testid="creator-sounds-row"
      >
        {tracks.map((t) => {
          const isPlaying = previewId === t.id;
          return (
            <div
              key={t.id}
              data-testid={`creator-sound-${t.id}`}
              className="snap-start flex-shrink-0 w-52 bg-[#0A0A0C] border border-white/10 hover:border-[#FF007F]/50 transition-all group"
            >
              <div className="relative w-full aspect-square bg-gradient-to-br from-[#FF007F]/25 via-black to-[#00FFFF]/20 flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-40 mix-blend-screen"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 30% 30%, rgba(255,0,127,0.4), transparent 60%), radial-gradient(circle at 70% 70%, rgba(0,255,255,0.35), transparent 60%)",
                  }}
                />
                <div className="absolute top-2 left-2 px-1.5 py-0.5 border border-[#FF007F] text-[#FF007F] bg-black/70 font-mono text-[9px] uppercase tracking-widest">
                  original
                </div>
                <div className="absolute top-2 right-2 flex items-center gap-1 font-mono text-[10px] text-white/80">
                  <Flame className="h-3 w-3 text-[#FF007F]" strokeWidth={2.5} />
                  {fmt(t.use_count || 0)}
                </div>
                <button
                  type="button"
                  data-testid={`creator-sound-preview-${t.id}`}
                  onClick={() => togglePreview(t)}
                  className={`relative z-10 h-12 w-12 border-2 flex items-center justify-center transition-all ${
                    isPlaying
                      ? "border-[#FF007F] bg-[#FF007F]/20 text-[#FF007F]"
                      : "border-white/70 text-white group-hover:border-[#FF007F] group-hover:text-[#FF007F]"
                  }`}
                  title={isPlaying ? "Pause" : "Preview"}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" fill="currentColor" />
                  ) : (
                    <Play className="h-5 w-5" fill="currentColor" />
                  )}
                </button>
                <Music
                  className="absolute bottom-2 right-2 h-4 w-4 text-white/40"
                  strokeWidth={1.5}
                />
              </div>

              <div className="p-3">
                <Link
                  to={`/sound/${encodeURIComponent(t.id)}`}
                  data-testid={`creator-sound-open-${t.id}`}
                  className="block"
                >
                  <p className="text-white text-sm font-semibold truncate hover:text-[#00FFFF] transition-colors">
                    {t.title}
                  </p>
                </Link>
                <div className="mt-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
                  <span className="text-[#FF007F]" data-testid={`creator-sound-remixes-${t.id}`}>
                    {fmt(t.remix_count)} {t.remix_count === 1 ? "remix" : "remixes"}
                  </span>
                  <span className="text-white/40">
                    {fmt(t.total_views)} views
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {isSelf && (
          <div
            data-testid="creator-sounds-cta"
            className="snap-start flex-shrink-0 w-52 border-2 border-dashed border-white/15 hover:border-[#FF007F]/60 hover:bg-[#FF007F]/5 flex flex-col items-center justify-center p-6 text-center transition-all"
          >
            <Music className="h-6 w-6 text-white/40 mb-2" strokeWidth={1.5} />
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              // share more
            </p>
            <Link
              to="/upload"
              className="mt-3 border border-[#FF007F]/60 hover:bg-[#FF007F]/10 text-[#FF007F] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5"
            >
              new video
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
